import { ReportFieldOutcome, ReportFieldState } from 'services/api/admin/portalProblemReports';

// Plain-language names for the extension's report vocabulary (olio-extension
// src/lib/report-events.ts). An unknown value falls through to itself, so a newer extension
// still reads, just less nicely.

export const FIELD_STATE_LABELS: Record<ReportFieldState['state'], string> = {
  auto: 'kept the auto-filled value',
  typed: 'typed it',
  typed_over_auto: 'typed over the auto-filled value',
  empty: 'left it empty',
};

export const CAPTURE_PATH_LABELS: Record<string, string> = {
  href_fetch: 'the document link',
  click: 'a click on the page',
  download_intercept: 'a browser download',
  new_tab: 'a new tab',
  navigation: 'the page loading the file',
  blob: 'a file the page built',
  data_url: 'inline data',
  local_file: 'a file from the computer',
  unknown: 'an unknown route',
};

export const CAPTURE_FAILURE_LABELS: Record<string, string> = {
  one_time_link: 'the link only worked once',
  not_a_file: 'a web page came back, not a file',
  content_mismatch: "the file wasn't the type it claimed",
  timeout: 'nothing downloaded',
  too_large: 'the file was too large',
  refused_origin: 'the file is on another host (refused)',
  unsupported_scheme: 'an unsupported kind of link',
  http_error: 'the portal answered with an error',
  fetch_failed: 'the download failed',
  no_longer_on_page: 'the document was gone from the page',
  left_page: 'the portal left the page (signed out?)',
  superseded: 'a newer download replaced it',
  ended: 'the page or job ended first',
  other: 'an unclassified failure',
};

const RUNG_LABELS: Record<string, string> = {
  labeled: 'its label',
  column: 'its column header',
  css: 'a CSS selector',
  testid: 'a test id',
  attribute: 'an attribute',
  text: 'its text',
  other: 'other',
};

export function rungLabel(rung: string) {
  return RUNG_LABELS[rung] ?? rung;
}

export function outcomeLabel(outcome: ReportFieldOutcome) {
  if (outcome.status === 'found') return 'Found';
  if (outcome.status === 'ambiguous') return `Matched ${outcome.matches} places`;
  return 'Not found';
}

// `d8`, `a14`, `x22`, `date10`, `time7`, `list3`, `empty`: a value's kind and length, never the value.
export function shapeLabel(shape: string) {
  if (shape === 'empty') return 'empty';
  const match = /^(date|time|list|d|a|x)(\d+)$/.exec(shape);
  if (!match) return shape;
  const [, kind, n] = match;
  switch (kind) {
    case 'd':
      return `${n} digits`;
    case 'a':
      return `${n} letters`;
    case 'date':
      return 'a date';
    case 'time':
      return 'a time';
    case 'list':
      return `${n} item${n === '1' ? '' : 's'}`;
    default:
      return `${n} characters`;
  }
}
